// Box ERP 공통 타입 정의

// ============================================
// 기본 타입
// ============================================

export type Status = 'draft' | 'pending' | 'approved' | 'completed' | 'cancelled';

export type DocumentStatus = {
  draft: '초안';
  pending: '대기중';
  approved: '승인됨';
  completed: '완료';
  cancelled: '취소됨';
};

export const STATUS_LABELS: DocumentStatus = {
  draft: '초안',
  pending: '대기중',
  approved: '승인됨',
  completed: '완료',
  cancelled: '취소됨',
};

export const STATUS_COLORS: Record<Status, string> = {
  draft: 'bg-gray-500',
  pending: 'bg-yellow-500',
  approved: 'bg-blue-500',
  completed: 'bg-green-500',
  cancelled: 'bg-red-500',
};

// ============================================
// 사용자 및 권한
// ============================================

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar?: string;
}

export type UserRole = 'admin' | 'manager' | 'user';

export const ROLE_LABELS: Record<UserRole, string> = {
  admin: '관리자',
  manager: '매니저',
  user: '사용자',
};

// ============================================
// 네비게이션
// ============================================

export type ModuleId =
  | 'dashboard'
  | 'pos'
  | 'stock'
  | 'selling'
  | 'buying'
  | 'accounts'
  | 'manufacturing'
  | 'crm'
  | 'projects'
  | 'settings';

export interface NavItem {
  id: ModuleId;
  label: string;
  icon: string;
  path: string;
  children?: NavItem[];
}

export interface BreadcrumbItem {
  label: string;
  path?: string;
}

// ============================================
// 테이블 및 데이터
// ============================================

export interface Column<T> {
  key: keyof T | string;
  label: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (value: unknown, row: T) => React.ReactNode;
}

export interface PaginationInfo {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface SortInfo {
  column: string;
  direction: 'asc' | 'desc';
}

export interface FilterInfo {
  column: string;
  value: string | number | boolean;
  operator: 'eq' | 'ne' | 'gt' | 'lt' | 'gte' | 'lte' | 'like' | 'in';
}

// ============================================
// 통화 및 금액
// ============================================

export interface Currency {
  code: string;
  symbol: string;
  name: string;
}

export const KRW: Currency = {
  code: 'KRW',
  symbol: '₩',
  name: '대한민국 원',
};

export function formatCurrency(amount: number, currency: Currency = KRW): string {
  return `${currency.symbol}${amount.toLocaleString('ko-KR')}`;
}

// ============================================
// 날짜 및 시간
// ============================================

export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

export function formatDateTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ============================================
// 공통 엔티티
// ============================================

export interface BaseEntity {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface Address {
  line1: string;
  line2?: string;
  city: string;
  state?: string;
  postalCode: string;
  country: string;
}

export interface Contact {
  name: string;
  phone?: string;
  email?: string;
  position?: string;
}

// ============================================
// 모듈별 페이지 타입
// ============================================

export type PageType =
  | 'list'      // 목록 페이지
  | 'form'      // 등록/수정 폼
  | 'detail'    // 상세 보기
  | 'dashboard' // 대시보드
  | 'report';   // 리포트

export interface PageConfig {
  title: string;
  type: PageType;
  module: ModuleId;
  breadcrumbs: BreadcrumbItem[];
}
